/**
 * Le navigateur OneDrive : choisir des pièces, les classer, les importer.
 *
 * **Le navigateur choisit, le serveur décide.** Cet écran n'envoie que trois
 * choses — le lecteur, l'élément, la catégorie. Le nom, la taille et la nature
 * qu'il affiche viennent de la source et n'ont aucune valeur pour la
 * sélection : le portail les relit avant de décider. Ce qu'on annoncerait ici
 * ne compte pas, et c'est voulu.
 *
 * **Le fil d'Ariane porte des chemins, pas des identifiants.** Remonter de
 * trois crans ne coûte donc aucun appel supplémentaire — le portail rend le
 * fil avec chaque page.
 *
 * **Le curseur est opaque** : on le renvoie tel qu'on l'a reçu, sans jamais
 * chercher à savoir ce qu'il porte.
 *
 * ## Ce qui a été repris pour que l'écran réponde
 *
 * Le dossier se liste en 300 ms — ce n'est pas la source qui traînait. Ce qui
 * donnait un écran poussif tenait à quatre choses, toutes dans le rendu :
 *
 * - **la liste disparaissait à chaque dossier ouvert**, remplacée par un
 *   squelette. Trois cents millisecondes de page blanche à chaque clic, et
 *   l'on perdait le repère de ce qu'on venait de quitter. Elle reste
 *   maintenant à l'écran, estompée, le temps que la suivante arrive ;
 * - **rien ne répondait au clic** : la ligne ouverte ne se distinguait pas, et
 *   l'on cliquait deux fois ;
 * - **le panneau de sélection poussait sans bornes**. À douze fichiers
 *   retenus, il occupait l'écran et la liste qu'on parcourait n'avait plus de
 *   place. Il est désormais borné et défile pour lui-même ;
 * - **chaque fichier se cochait un par un**, sans « tout prendre » ni plage.
 *   Douze CCTP dans un dossier faisaient douze clics et une erreur d'attention.
 *
 * Et le dossier de plus de cent éléments n'est plus un cul-de-sac : le portail
 * rend un curseur, on s'en sert, au lieu de demander au métreur de réorganiser
 * son OneDrive.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { CATEGORIES, type Connexion, type Element, type Parcours } from "../../api/types";
import { Avis } from "../../composants/avis";
import { Chargement, Vide } from "../../composants/etats";
import { poids, quand } from "../../composants/formats";

/** Où le portail devra nous ramener après le détour par Microsoft.
 *
 * L'adresse courante porte déjà `?onedrive=<categorie>` : on revient donc sur
 * l'affaire, la boîte rouverte, la catégorie intacte. Le serveur n'accepte
 * qu'un chemin de chez lui — voir `web/auth/retours.py` — et c'est ce qu'on
 * lui donne.
 *
 * Un vrai `<a href>`, non le routeur client : on quitte l'application. */
function lienDeLiaison(): string {
  const ici = window.location.pathname + window.location.search;
  return `/auth/onedrive/link?suite=${encodeURIComponent(ici)}`;
}

interface Choix {
  element: Element;
  categorie: string;
}

export function NavigateurOneDrive({
  affaireId,
  categorieInitiale,
  fermer,
  importe,
}: {
  affaireId: string;
  categorieInitiale: string;
  fermer: () => void;
  importe: () => void;
}) {
  const [connexions, poserConnexions] = useState<Connexion[] | null>(null);
  const [connexion, poserConnexion] = useState<string>("");
  const [chemin, poserChemin] = useState<string>("");
  const [parcours, poserParcours] = useState<Parcours | null>(null);
  // **Deux états de chargement, non un seul.** Le premier justifie un
  // squelette — il n'y a rien à montrer ; le second ne le justifie pas — il y
  // a la liste précédente, et l'effacer coûte plus qu'elle ne rapporte.
  const [charge, poserCharge] = useState(true);
  const [va, poserVa] = useState<string | null>(null);
  const [erreur, poserErreur] = useState("");
  const [choix, poserChoix] = useState<Choix[]>([]);
  const [envoi, poserEnvoi] = useState(false);
  const [avance, poserAvance] = useState(0);
  const [issues, poserIssues] = useState<string[]>([]);
  const [filtre, poserFiltre] = useState("");
  // Le dernier fichier basculé, pour la sélection par plage : Maj+clic prend
  // tout ce qui va de là jusqu'ici.
  const dernier = useRef<string | null>(null);

  useEffect(() => {
    api
      .lire<{ donnees: Connexion[] }>("/auth/onedrive")
      .then((issue) => {
        const liste = issue.donnees ?? [];
        poserConnexions(liste);
        const vivante = liste.find((c) => c.utilisable);
        if (vivante) poserConnexion(vivante.connexion_id);
      })
      .catch(() => poserConnexions([]));
  }, []);

  const parcourir = useCallback(
    async (versChemin: string) => {
      if (!connexion) return;
      poserVa(versChemin);
      poserErreur("");
      try {
        const issue = await api.lire<Parcours>(
          `/api/v1/onedrive/${connexion}/enfants?chemin=${encodeURIComponent(versChemin)}`,
        );
        poserParcours(issue);
        poserChemin(versChemin);
        // Le filtre porte sur le dossier qu'on regarde : le garder en
        // changeant de dossier cacherait ce qu'on vient d'ouvrir.
        poserFiltre("");
        dernier.current = null;
      } catch (exc) {
        poserErreur(
          exc instanceof ErreurApi
            ? exc.message
            : "Le dossier n'a pas pu être ouvert.",
        );
      } finally {
        poserCharge(false);
        poserVa(null);
      }
    },
    [connexion],
  );

  /** La page suivante du dossier courant, ajoutée à celle qu'on lit.
   *
   * L'écran disait « affinez par sous-dossier » : c'était demander au métreur
   * de réorganiser son OneDrive pour arranger le portail, alors que le
   * curseur était déjà rendu. */
  const encore = useCallback(async () => {
    if (!connexion || !parcours?.suite) return;
    poserVa(chemin);
    try {
      const issue = await api.lire<Parcours>(
        `/api/v1/onedrive/${connexion}/enfants?chemin=${encodeURIComponent(chemin)}`
        + `&curseur=${encodeURIComponent(parcours.suite)}`,
      );
      poserParcours((avant) =>
        avant ? { ...issue, elements: [...avant.elements, ...issue.elements] }
              : issue);
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message : "La suite n'a pas pu être lue.",
      );
    } finally {
      poserVa(null);
    }
  }, [connexion, chemin, parcours?.suite]);

  useEffect(() => {
    if (connexion) void parcourir("");
  }, [connexion, parcourir]);

  // **Échap ferme.** Une boîte modale qu'on ne peut quitter qu'à la souris
  // oblige à viser un bouton pour renoncer. Pas pendant l'import : la fermer
  // en cours de route laisserait le métreur sans le compte rendu.
  useEffect(() => {
    const touche = (evenement: KeyboardEvent) => {
      if (evenement.key === "Escape" && !envoi) fermer();
    };
    window.addEventListener("keydown", touche);
    return () => window.removeEventListener("keydown", touche);
  }, [envoi, fermer]);

  const elements = parcours?.elements ?? [];
  const visibles = useMemo(() => {
    const terme = filtre.trim().toLowerCase();
    if (!terme) return elements;
    // Les dossiers restent visibles : filtrer sur un nom de fichier ne doit
    // pas fermer le chemin qui y mène.
    return elements.filter(
      (e) => e.dossier || e.nom.toLowerCase().includes(terme),
    );
  }, [elements, filtre]);

  const retenus = useMemo(
    () => new Set(choix.map((c) => c.element.item_id)),
    [choix],
  );

  function prendre(liste: Element[], prendreLes: boolean) {
    poserChoix((deja) => {
      const ids = new Set(liste.map((e) => e.item_id));
      if (!prendreLes) return deja.filter((c) => !ids.has(c.element.item_id));
      const dedans = new Set(deja.map((c) => c.element.item_id));
      return [
        ...deja,
        ...liste.filter((e) => !dedans.has(e.item_id))
                .map((e) => ({ element: e, categorie: categorieInitiale })),
      ];
    });
  }

  /** Bascule un fichier, ou toute la plage depuis le dernier si Maj est tenue. */
  function basculer(element: Element, plage = false) {
    const fichiers = visibles.filter((e) => !e.dossier);
    if (plage && dernier.current) {
      const depuis = fichiers.findIndex((e) => e.item_id === dernier.current);
      const jusqua = fichiers.findIndex((e) => e.item_id === element.item_id);
      if (depuis !== -1 && jusqua !== -1) {
        const [a, b] = depuis < jusqua ? [depuis, jusqua] : [jusqua, depuis];
        prendre(fichiers.slice(a, b + 1), !retenus.has(element.item_id));
        dernier.current = element.item_id;
        return;
      }
    }
    prendre([element], !retenus.has(element.item_id));
    dernier.current = element.item_id;
  }

  function classer(itemId: string, categorie: string) {
    poserChoix((deja) =>
      deja.map((c) => (c.element.item_id === itemId ? { ...c, categorie } : c)),
    );
  }

  async function importer() {
    poserEnvoi(true);
    poserAvance(0);
    poserErreur("");
    const rapport: string[] = [];
    for (const [rang, { element, categorie }] of choix.entries()) {
      // **Le compte avance avant l'appel, non après.** « 3 sur 7 » veut dire
      // « la troisième part » ; l'afficher après l'aller-retour laisserait
      // l'écran à « 2 sur 7 » pendant qu'il travaille.
      poserAvance(rang + 1);
      try {
        await api.ecrire(`/api/v1/affaires/${affaireId}/pieces`, {
          connexion_id: connexion,
          drive_id: element.drive_id,
          item_id: element.item_id,
          categorie,
        });
      } catch (exc) {
        // **Une pièce refusée n'arrête pas les autres.** Le métreur en a
        // sélectionné cinq ; lui en refuser cinq parce qu'une seule ne
        // convient pas lui ferait tout recommencer.
        rapport.push(
          `${element.nom} — ${exc instanceof ErreurApi ? exc.message : "refusée"}`,
        );
      }
    }
    poserEnvoi(false);
    poserAvance(0);
    poserIssues(rapport);
    if (rapport.length === 0) {
      importe();
      fermer();
    } else {
      importe();
      poserChoix((deja) =>
        deja.filter((c) => rapport.some((r) => r.startsWith(c.element.nom))),
      );
    }
  }

  // **Ce qui compte est une connexion *utilisable*, non une connexion.**
  // Avec un compte lié mais à reconsentir, la liste n'était pas vide — donc
  // « aucun compte lié » était faux — et aucune n'était retenue — donc le
  // parcours ne partait jamais. L'écran affichait « Chargement… » sans fin,
  // et sans rien charger.
  const utilisables = connexions?.filter((c) => c.utilisable) ?? null;
  const aucuneUtilisable = utilisables !== null && utilisables.length === 0;
  const liees = connexions?.length ?? 0;
  const sansCategorie = choix.filter((c) => !c.categorie);
  const fichiers = visibles.filter((e) => !e.dossier);
  const tousPris = fichiers.length > 0
    && fichiers.every((e) => retenus.has(e.item_id));

  return (
    <div className="voile" role="dialog" aria-modal="true" aria-label="Ajouter des sources">
      <div className="modale modale--picker">
        <div className="modale__tete">
          <div className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
            <h2>Ajouter des sources depuis OneDrive</h2>
            {parcours && (
              <nav className="fil" aria-label="Fil d'Ariane">
                {parcours.fil.map((cran, rang) => (
                  <span key={cran.chemin || "racine"}>
                    {rang > 0 && <span aria-hidden="true"> / </span>}
                    <button type="button" onClick={() => void parcourir(cran.chemin)}>
                      {cran.nom}
                    </button>
                  </span>
                ))}
              </nav>
            )}
          </div>
          {utilisables && utilisables.length > 1 && (
            <select
              value={connexion}
              onChange={(e) => poserConnexion(e.target.value)}
              aria-label="Compte OneDrive"
            >
              {utilisables.map((c) => (
                <option key={c.connexion_id} value={c.connexion_id}>
                  {c.compte}
                </option>
              ))}
            </select>
          )}
        </div>

        {!aucuneUtilisable && !erreur && !charge && (
          <div className="picker__outils">
            <label className="champ-recherche champ-recherche--court">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="search"
                value={filtre}
                placeholder="Filtrer ce dossier…"
                aria-label="Filtrer ce dossier"
                onChange={(evenement) => poserFiltre(evenement.target.value)}
              />
            </label>
            <span className="discret">
              {fichiers.length} fichier(s)
              {filtre && ` sur ${elements.filter((e) => !e.dossier).length}`}
            </span>
            <span style={{ flex: 1 }} />
            {/* Le fil qui dit qu'il se passe quelque chose sans rien effacer. */}
            {va !== null && <span className="picker__travail">Chargement…</span>}
          </div>
        )}

        <div className="modale__corps">
          {aucuneUtilisable ? (
            <Vide
              titre={
                liees === 0
                  ? "Aucun compte OneDrive lié"
                  : "Votre compte OneDrive doit être reconnecté"
              }
              aide={
                liees === 0
                  ? "Liez votre compte pour parcourir vos fichiers depuis la "
                    + "plateforme."
                  : `L'accès n'est plus valable (${
                      connexions?.map((c) => `${c.compte} — ${c.sante}`).join(", ")
                    }). Reconnectez-le : les pièces déjà importées restent.`
              }
              action={
                <a className="bouton bouton--primaire" href={lienDeLiaison()}>
                  {liees === 0
                    ? "Connecter mon OneDrive"
                    : "Reconnecter mon OneDrive"}
                </a>
              }
            />
          ) : erreur ? (
            <div style={{ padding: "var(--mo-space-5)" }}>
              <Avis ton="erreur" cle={erreur}>{erreur}</Avis>
            </div>
          ) : charge ? (
            <div style={{ padding: "var(--mo-space-5)" }}>
              <Chargement lignes={5} />
            </div>
          ) : (
            <table className={`tableau${va !== null ? " tableau--attente" : ""}`}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    {/* Prendre tout le dossier d'un geste. Absente quand il
                        n'y a que des sous-dossiers : une case qui ne coche
                        rien invite à un clic sans effet. */}
                    {fichiers.length > 0 && (
                      <input
                        type="checkbox"
                        checked={tousPris}
                        ref={(n) => {
                          if (n) {
                            n.indeterminate = !tousPris
                              && fichiers.some((e) => retenus.has(e.item_id));
                          }
                        }}
                        onChange={() => prendre(fichiers, !tousPris)}
                        aria-label={
                          tousPris
                            ? "Désélectionner tous les fichiers"
                            : "Sélectionner tous les fichiers de ce dossier"
                        }
                      />
                    )}
                  </th>
                  <th>Nom</th>
                  <th>Modifié</th>
                  <th>Taille</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((element) => {
                  const retenu = retenus.has(element.item_id);
                  const ouvre = element.dossier
                    && va === (chemin ? `${chemin}/${element.nom}` : element.nom);
                  return (
                    <tr
                      key={element.item_id}
                      className={
                        `cliquable${retenu ? " liste__ligne--visee" : ""}`
                        + (ouvre ? " picker__ligne--ouvre" : "")
                      }
                      onClick={(evenement) =>
                        element.dossier
                          ? void parcourir(
                              chemin ? `${chemin}/${element.nom}` : element.nom,
                            )
                          : basculer(element, evenement.shiftKey)
                      }
                    >
                      <td>
                        {element.dossier ? (
                          <span aria-hidden="true">📁</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={retenu}
                            onChange={() => undefined}
                            onClick={(evenement) => {
                              evenement.stopPropagation();
                              basculer(element, evenement.shiftKey);
                            }}
                            aria-label={`Sélectionner ${element.nom}`}
                          />
                        )}
                      </td>
                      <td>{element.nom}</td>
                      <td className="discret nombre">{quand(element.modifie_le)}</td>
                      <td className="discret nombre">
                        {element.dossier ? "—" : poids(element.octets)}
                      </td>
                    </tr>
                  );
                })}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <Vide
                        titre={
                          filtre
                            ? `Aucun fichier pour « ${filtre} »`
                            : "Ce dossier est vide"
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {parcours?.suite && !charge && (
            <div className="picker__suite">
              <button type="button" className="bouton" disabled={va !== null}
                      onClick={() => void encore()}>
                {va !== null ? "Chargement…" : "Charger la suite du dossier"}
              </button>
            </div>
          )}
        </div>

        {choix.length > 0 && (
          <div className="picker__panier">
            <div className="rangee rangee--espacee">
              <strong>Sélection à importer</strong>
              <span className="discret">Classez chaque pièce avant confirmation.</span>
            </div>
            {/* **Borné, et il défile pour lui-même.** Sans cela, douze fichiers
                retenus chassaient de l'écran la liste qu'on parcourait — plus
                on sélectionnait, moins on pouvait sélectionner. */}
            <div className="picker__lignes">
              {choix.map(({ element, categorie }) => (
                <div key={element.item_id} className="rangee">
                  <button
                    type="button"
                    className="action"
                    title="Retirer de la sélection"
                    aria-label={`Retirer ${element.nom} de la sélection`}
                    onClick={() => prendre([element], false)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                      <path d="m6 6 12 12M18 6 6 18" />
                    </svg>
                  </button>
                  <span className="tronque" style={{ flex: 1 }}>{element.nom}</span>
                  <span className="faible nombre">{poids(element.octets)}</span>
                  <select
                    value={categorie}
                    onChange={(e) => classer(element.item_id, e.target.value)}
                    aria-label={`Catégorie de ${element.nom}`}
                  >
                    <option value="">— catégorie requise —</option>
                    {CATEGORIES.map((c) => (
                      <option key={c.clef} value={c.clef}>{c.libelle}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {issues.length > 0 && (
              <Avis ton="attention" cle={issues.join("|")}>
                <div className="pile pile--serree">
                  <strong>{issues.length} pièce(s) refusée(s) :</strong>
                  {issues.map((ligne) => (
                    <span key={ligne} className="discret">{ligne}</span>
                  ))}
                </div>
              </Avis>
            )}
          </div>
        )}

        <div className="modale__pied">
          <span className="discret" style={{ flex: 1 }}>
            {envoi
              ? `Import ${avance} sur ${choix.length}…`
              : choix.length > 0 && `${choix.length} fichier(s) sélectionné(s)`}
          </span>
          <button type="button" className="bouton" onClick={fermer} disabled={envoi}>
            Annuler
          </button>
          <button
            type="button"
            className="bouton bouton--primaire"
            disabled={choix.length === 0 || sansCategorie.length > 0 || envoi}
            onClick={importer}
          >
            {envoi ? `Import ${avance}/${choix.length}…` : "Importer dans l'affaire"}
          </button>
        </div>
      </div>
    </div>
  );
}
